import React, { useState, useRef, useEffect } from 'react';
import { RestaurantConfig, CategoryDefinition, TimeRange, DesignMode, PackageBooking } from '../types';
import { X, Plus, Trash2, Save, Eye, EyeOff, List, Filter, Palette, Sparkles, Layout, Users, Minus, Store, Clock, Info, Gamepad2, Upload, Image, Share2, Moon, Sun, UserCheck, ShieldCheck, LayoutGrid, Settings, MapPin, Package, CalendarDays, Zap, Phone, Mail, Globe, Facebook, Twitter, Music2, Instagram, Calendar, Repeat, Bold, Italic, Underline, List as ListIcon, ListOrdered as ListOrderedIcon, Link as LinkIcon, AlignLeft, AlignCenter, AlignRight, ChevronUp, ChevronDown, Square, Type, Eraser, Minus as HorizontalLine, Highlighter, Heading1, Heading2, Pilcrow, Loader2, Rocket } from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (val: string) => void;
  label?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, label }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const exec = (command: string, arg: string | undefined = undefined) => {
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    handleInput();
  };

  const handleInput = () => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  };

  const ToolbarButton = ({ icon: Icon, cmd, arg, title, active = false }: { icon: any, cmd: string, arg?: string, title: string, active?: boolean }) => (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => {
        e.preventDefault();
        exec(cmd, arg);
      }}
      className={`p-2 rounded hover:bg-slate-200 transition-colors flex items-center justify-center ${active ? 'bg-slate-200 text-indigo-600' : 'text-slate-600'}`}
    >
      <Icon size={16} />
    </button>
  );

  return (
    <div className="space-y-1.5 animate-fade-in">
      {label && <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{label}</label>}
      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm flex flex-col">
        <style>{`
          .rte-content ul { list-style-type: disc !important; padding-left: 1.5rem !important; margin-bottom: 0.5rem; }
          .rte-content ol { list-style-type: decimal !important; padding-left: 1.5rem !important; margin-bottom: 0.5rem; }
          .rte-content li { margin-bottom: 0.25rem; }
          .rte-content h1 { font-size: 1.5rem; font-weight: 800; margin-bottom: 0.5rem; line-height: 1.2; margin-top: 0.5rem; }
          .rte-content h2 { font-size: 1.25rem; font-weight: 700; margin-bottom: 0.5rem; line-height: 1.3; margin-top: 0.5rem; }
          .rte-content p { margin-bottom: 0.5rem; line-height: 1.5; }
          .rte-content a { color: #2563eb; text-decoration: underline; }
          .rte-content blockquote { border-left: 4px solid #e2e8f0; padding-left: 1rem; color: #64748b; font-style: italic; margin-left: 0; }
        `}</style>

        <div className="bg-slate-50 border-b border-slate-200 p-1.5 flex flex-wrap gap-0.5 items-center">
          <div className="flex bg-white rounded border border-slate-200 p-0.5 mr-1 gap-0.5">
            <ToolbarButton icon={Heading1} cmd="formatBlock" arg="<h1>" title="Überschrift 1 (H1)" />
            <ToolbarButton icon={Heading2} cmd="formatBlock" arg="<h2>" title="Überschrift 2 (H2)" />
            <ToolbarButton icon={Pilcrow} cmd="formatBlock" arg="<p>" title="Standard Text (P)" />
          </div>

          <div className="flex gap-0.5 mr-1">
            <ToolbarButton icon={Bold} cmd="bold" title="Fett" />
            <ToolbarButton icon={Italic} cmd="italic" title="Kursiv" />
            <ToolbarButton icon={Underline} cmd="underline" title="Unterstrichen" />
          </div>

          <div className="w-px h-6 bg-slate-200 mx-1 self-center" />

          <div className="flex gap-0.5 mr-1">
            <ToolbarButton icon={ListIcon} cmd="insertUnorderedList" title="Aufzählung" />
            <ToolbarButton icon={ListOrderedIcon} cmd="insertOrderedList" title="Nummerierung" />
          </div>

          <div className="w-px h-6 bg-slate-200 mx-1 self-center" />

          <div className="flex gap-0.5 mr-1">
            <ToolbarButton icon={AlignLeft} cmd="justifyLeft" title="Linksbündig" />
            <ToolbarButton icon={AlignCenter} cmd="justifyCenter" title="Zentriert" />
            <ToolbarButton icon={AlignRight} cmd="justifyRight" title="Rechtsbündig" />
          </div>

          <div className="w-px h-6 bg-slate-200 mx-1 self-center" />

          <div className="flex gap-1 items-center">
            <div className="relative group/color">
               <Palette size={16} className="text-slate-500 absolute left-2 top-2 pointer-events-none" />
               <input 
                 type="color" 
                 title="Textfarbe"
                 onChange={(e) => exec('foreColor', e.target.value)}
                 className="w-8 h-8 rounded border border-slate-200 cursor-pointer p-0 opacity-0"
               />
               <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                 <Palette size={16} className="text-slate-500" />
               </div>
            </div>
            
            <div className="relative group/color">
               <Highlighter size={16} className="text-slate-500 absolute left-2 top-2 pointer-events-none" />
               <input 
                 type="color" 
                 title="Texthintergrund"
                 onChange={(e) => exec('hiliteColor', e.target.value)}
                 className="w-8 h-8 rounded border border-slate-200 cursor-pointer p-0 opacity-0"
               />
               <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                 <Highlighter size={16} className="text-slate-500" />
               </div>
            </div>
          </div>

          <div className="w-px h-6 bg-slate-200 mx-1 self-center" />

          <div className="flex gap-0.5">
            <button
              type="button"
              title="Link einfügen"
              onMouseDown={(e) => {
                e.preventDefault();
                const url = window.prompt('URL eingeben (z.B. https://google.de):');
                if (url) exec('createLink', url);
              }}
              className="p-2 rounded hover:bg-slate-200 text-slate-600 transition-colors"
            >
              <LinkIcon size={16} />
            </button>
            <ToolbarButton icon={HorizontalLine} cmd="insertHorizontalRule" title="Trennlinie" />
            <ToolbarButton icon={Eraser} cmd="removeFormat" title="Formatierung löschen" />
          </div>
        </div>

        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onBlur={handleInput}
          className="min-h-[220px] p-6 text-sm outline-none rte-content max-w-none text-slate-800 focus:bg-white transition-colors overflow-y-auto"
          style={{ height: 'auto' }}
        />
      </div>
      <div className="flex justify-between items-center px-1">
        <p className="text-[9px] text-slate-400 italic">Nutzen Sie die Toolbar oben, um das Paket detailreich zu beschreiben.</p>
        <span className="text-[9px] font-black text-slate-300 uppercase">{value.length} Zeichen</span>
      </div>
    </div>
  );
};

interface AdminSettingsProps {
  config: RestaurantConfig;
  categories: CategoryDefinition[];
  onUpdateConfig: (config: RestaurantConfig) => void;
  onUpdateCategories: (categories: CategoryDefinition[]) => void;
  onClose: () => void;
}

export const AdminSettings: React.FC<AdminSettingsProps> = ({ 
  config, 
  categories, 
  onUpdateConfig, 
  onUpdateCategories,
  onClose 
}) => {
  const [localConfig, setLocalConfig] = useState<RestaurantConfig>({ ...config });
  const [localCategories, setLocalCategories] = useState<CategoryDefinition[]>([...categories]);
  const [activeTab, setActiveTab] = useState<'GENERAL' | 'DESIGN' | 'SCHEDULE' | 'CATEGORIES' | 'PACKAGES' | 'UPGRADE'>('GENERAL');
  const [expandedPackageInfo, setExpandedPackageInfo] = useState<Record<string, boolean>>({});
  const [isUploading, setIsUploading] = useState(false);

  const handleSave = () => {
    onUpdateConfig(localConfig);
    onUpdateCategories(localCategories);
    onClose();
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    const fileName = `${Date.now()}-${file.name.replace(/\s+/g, '_')}`;
    const filePath = `logos/${fileName}`;
    const bucket = 'images';

    try {
      // Supabase Storage Upload via REST API
      const supabaseUrl = (globalThis as any).process?.env?.SUPABASE_URL;
      const supabaseKey = (globalThis as any).process?.env?.SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseKey) {
        throw new Error("Supabase credentials missing in environment variables.");
      }

      const uploadUrl = `${supabaseUrl}/storage/v1/object/${bucket}/${filePath}`;
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey,
          'Content-Type': file.type
        },
        body: file
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Upload failed");
      }

      // Construct public URL (assuming the bucket is public)
      const publicUrl = `${supabaseUrl}/storage/v1/object/public/${bucket}/${filePath}`;
      setLocalConfig(prev => ({ ...prev, logoUrl: publicUrl }));
    } catch (err) {
      console.error("Supabase Upload Error:", err);
      alert("Logo-Upload fehlgeschlagen. Bitte prüfen Sie die Supabase-Konfiguration.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleModeChange = (mode: DesignMode) => {
    let newTheme = { ...localConfig.theme, mode };

    if (mode === 'SIMPLE') {
        newTheme.primaryColor = '#475569'; 
        newTheme.secondaryColor = '#0f172a'; 
    } else {
        newTheme.primaryColor = '#10b981'; 
        newTheme.secondaryColor = '#064e3b'; 
    }

    setLocalConfig({
        ...localConfig,
        theme: newTheme
    });
  };

  const toggleSocialProvider = (provider: string) => {
    // Apple is strictly disabled
    if (provider === 'apple') return;
    
    const current = localConfig.socialProviders || [];
    if (current.includes(provider)) {
        setLocalConfig({ ...localConfig, socialProviders: current.filter(p => p !== provider) });
    } else {
        setLocalConfig({ ...localConfig, socialProviders: [...current, provider] });
    }
  };

  const handleAddCategory = () => {
    const newCat: CategoryDefinition = {
      id: `CAT_${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
      name: 'Neue Sportart',
      color: '#e2e8f0',
      borderColor: '#94a3b8',
      icon: '⚽',
      minDuration: 60,
      defaultSeats: 2
    };
    setLocalCategories([...localCategories, newCat]);
  };

  const updateCategory = (id: string, field: keyof CategoryDefinition, value: string | number) => {
    setLocalCategories(prev => prev.map(cat => 
      cat.id === id ? { ...cat, [field]: value } : cat
    ));
  };

  const deleteCategory = (id: string) => {
    if (window.confirm('Sportart wirklich löschen?')) {
        setLocalCategories(prev => prev.filter(cat => cat.id !== id));
    }
  };

  const handleAddPackage = () => {
    const newPkg: PackageBooking = {
      id: `PKG_${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
      name: 'Neues Paket',
      icon: '🎁',
      categoryId: localCategories.length > 0 ? localCategories[0].id : '',
      allowedDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      startTime: '00:00',
      endTime: '23:59',
      defaultPartySize: 2,
      durationMinutes: 60,
      extendedInfo: ''
    };
    setLocalConfig(prev => ({ ...prev, packages: [...(prev.packages || []), newPkg] }));
  };

  const updatePackage = (id: string, updates: Partial<PackageBooking>) => {
    setLocalConfig(prev => ({
      ...prev,
      packages: (prev.packages || []).map(pkg => 
        pkg.id === id ? { ...pkg, ...updates } : pkg
      )
    }));
  };

  const deletePackage = (id: string) => {
    if (window.confirm('Paket wirklich löschen?')) {
      setLocalConfig(prev => ({
        ...prev,
        packages: (prev.packages || []).filter(pkg => pkg.id !== id)
      }));
    }
  };

  const togglePackageDay = (pkgId: string, day: string) => {
    const pkg = (localConfig.packages || []).find(p => p.id === pkgId);
    if (!pkg) return;
    const currentDays = pkg.allowedDays || [];
    const newDays = currentDays.includes(day) 
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];
    updatePackage(pkgId, { allowedDays: newDays });
  };

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  const getDayLabel = (day: string) => {
    const map: Record<string, string> = {
      Monday: 'Mo', Tuesday: 'Di', Wednesday: 'Mi',
      Thursday: 'Do', Friday: 'Fr', Saturday: 'Sa', Sunday: 'So'
    };
    return map[day] || day;
  };

  const updateSchedule = (day: string, field: keyof TimeRange, value: any) => {
    setLocalConfig(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule[day],
          [field]: value
        }
      }
    }));
  };

  const ToggleSwitch = ({ checked, onChange, label, disabled = false }: { checked: boolean; onChange: () => void; label?: React.ReactNode; disabled?: boolean }) => (
    <label className={`flex items-center gap-3 select-none group ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        <div 
            className={`w-10 h-5 rounded-full relative transition-colors flex-shrink-0 ${checked ? 'bg-[var(--color-primary)]' : 'bg-slate-300'} ${disabled ? 'opacity-40 grayscale' : ''}`}
            onClick={(e) => {
                if (disabled) return;
                e.preventDefault();
                e.stopPropagation();
                onChange();
            }}
        >
            <div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-all ${checked ? 'left-6' : 'left-1'}`} />
        </div>
        {label && (
          <div className={`text-sm ${disabled ? 'text-slate-400' : 'text-slate-700 group-hover:text-slate-900'} transition-colors`}>
            {label}
          </div>
        )}
    </label>
  );

  const tabClass = (isActive: boolean) => 
    `px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 flex items-center justify-center ${isActive ? 'border-[var(--color-primary)] text-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-transparent text-slate-600 hover:text-slate-800 hover:bg-slate-50'}`;

  const getArenaTimeBounds = () => {
    let minTime = '23:59';
    let maxTime = '00:00';
    let hasOpenDays = false;

    Object.values(localConfig.schedule).forEach((s: TimeRange) => {
      if (!s.isClosed) {
        hasOpenDays = true;
        if (s.open < minTime) minTime = s.open;
        if (s.close > maxTime) maxTime = s.close;
      }
    });

    return hasOpenDays ? { min: minTime, max: maxTime } : { min: '00:00', max: '23:59' };
  };

  const arenaBounds = getArenaTimeBounds();

  const isTimeRestricted = (pkg: PackageBooking) => {
    return (pkg.startTime !== '00:00') || (pkg.endTime !== '23:59');
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center bg-black/50 backdrop-blur-sm px-4 pt-20">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col overflow-hidden animate-fade-in">
        <div className="flex items-center justify-between p-6 border-b border-slate-100 flex-shrink-0">
          <h2 className="text-xl font-bold text-slate-800">Einstellungen</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500">
            <X size={20} />
          </button>
        </div>

        <div className="flex border-b border-slate-100 px-6 overflow-x-auto gap-2 bg-white flex-shrink-0">
          <button onClick={() => setActiveTab('GENERAL')} className={tabClass(activeTab === 'GENERAL')}><Store size={16} className="md:mr-2" /> <span className="hidden md:inline">Allgemein</span></button>
          <button onClick={() => setActiveTab('DESIGN')} className={tabClass(activeTab === 'DESIGN')}><Palette size={16} className="md:mr-2" /> <span className="hidden md:inline">Design</span></button>
          <button onClick={() => setActiveTab('SCHEDULE')} className={tabClass(activeTab === 'SCHEDULE')}><Clock size={16} className="md:mr-2" /> <span className="hidden md:inline">Öffnungszeiten & Reservierungen</span></button>
          <button onClick={() => setActiveTab('CATEGORIES')} className={tabClass(activeTab === 'CATEGORIES')}><LayoutGrid size={16} className="md:mr-2" /> <span className="hidden md:inline">Sportarten</span></button>
          <button onClick={() => setActiveTab('PACKAGES')} className={tabClass(activeTab === 'PACKAGES')}><Package size={16} className="md:mr-2" /> <span className="hidden md:inline">Paketbuchungen</span></button>
          <button onClick={() => setActiveTab('UPGRADE')} className={tabClass(activeTab === 'UPGRADE')}><Rocket size={16} className="md:mr-2" /> <span className="hidden md:inline">Upgrade</span></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-slate-50">
          {activeTab === 'GENERAL' && (
            <div className="space-y-6">
              <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2 pb-2 border-b border-slate-100">
                   <Store size={16} className="text-[var(--color-primary)]" /> Halleninfos
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Name der Halle</label>
                    <input type="text" value={localConfig.name} onChange={e => setLocalConfig({...localConfig, name: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-[var(--color-primary)] outline-none bg-white text-slate-800" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Inhaber Name</label>
                    <input type="text" value={localConfig.ownerName || ''} onChange={e => setLocalConfig({...localConfig, ownerName: e.target.value})} placeholder="Vorname Nachname" className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-[var(--color-primary)] outline-none bg-white text-slate-800" />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-2 text-slate-500"><MapPin size={12} /> Adresse der Halle</label>
                  <input type="text" value={localConfig.address || ''} onChange={e => setLocalConfig({...localConfig, address: e.target.value})} placeholder="Straße Hausnr., PLZ Ort" className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-[var(--color-primary)] outline-none bg-white text-slate-800" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-2 text-slate-500"><Phone size={12} /> Telefon (Festnetz)</label>
                    <input type="text" value={localConfig.phoneLandline || ''} onChange={e => setLocalConfig({...localConfig, phoneLandline: e.target.value})} placeholder="z.B. 030 1234567" className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-[var(--color-primary)] outline-none bg-white text-slate-800" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-2 text-slate-500"><Phone size={12} /> Mobilnummer</label>
                    <input type="text" value={localConfig.phoneMobile || ''} onChange={e => setLocalConfig({...localConfig, phoneMobile: e.target.value})} placeholder="z.B. 0170 9876543" className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-[var(--color-primary)] outline-none bg-white text-slate-800" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-2 text-slate-500"><Mail size={12} /> Email (Kontakt)</label>
                    <input type="email" value={localConfig.contactEmail || ''} onChange={e => setLocalConfig({...localConfig, contactEmail: e.target.value})} placeholder="z.B. info@sportarena.de" className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-[var(--color-primary)] outline-none bg-white text-slate-800" />
                  </div>
                </div>

                <div className="mb-6 pt-4 border-t border-slate-100">
                    <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2"><Globe size={16} className="text-[var(--color-primary)]" /> Online-Präsenz</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-1 flex items-center gap-1 text-slate-500"><Globe size={10} /> Webseite</label><input type="url" value={localConfig.websiteUrl || ''} onChange={e => setLocalConfig({...localConfig, websiteUrl: e.target.value})} placeholder="https://..." className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-[var(--color-primary)] outline-none bg-white text-sm text-slate-800" /></div>
                        <div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-1 flex items-center gap-1 text-slate-500"><Instagram size={10} /> Instagram</label><input type="url" value={localConfig.instagramUrl || ''} onChange={e => setLocalConfig({...localConfig, instagramUrl: e.target.value})} placeholder="https://instagram.com/..." className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-[var(--color-primary)] outline-none bg-white text-sm text-slate-800" /></div>
                        <div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-1 flex items-center gap-1 text-slate-500"><Facebook size={10} /> Facebook</label><input type="url" value={localConfig.facebookUrl || ''} onChange={e => setLocalConfig({...localConfig, facebookUrl: e.target.value})} placeholder="https://facebook.com/..." className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-[var(--color-primary)] outline-none bg-white text-sm text-slate-800" /></div>
                        <div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-1 flex items-center gap-1 text-slate-500"><Twitter size={10} /> Twitter / X</label><input type="url" value={localConfig.twitterUrl || ''} onChange={e => setLocalConfig({...localConfig, twitterUrl: e.target.value})} placeholder="https://twitter.com/..." className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-[var(--color-primary)] outline-none bg-white text-sm text-slate-800" /></div>
                        <div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-1 flex items-center gap-1 text-slate-500"><Music2 size={10} /> TikTok</label><input type="url" value={localConfig.tiktokUrl || ''} onChange={e => setLocalConfig({...localConfig, tiktokUrl: e.target.value})} placeholder="https://tiktok.com/@..." className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-[var(--color-primary)] outline-none bg-white text-sm text-slate-800" /></div>
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="col-span-1">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Logo (Supabase Storage)</label>
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 transition-colors relative h-32 overflow-hidden">
                            {isUploading ? (
                              <div className="flex flex-col items-center gap-2">
                                <Loader2 className="animate-spin text-emerald-500" size={24} />
                                <span className="text-[10px] text-slate-500 font-bold uppercase">Wird hochgeladen...</span>
                              </div>
                            ) : localConfig.logoUrl ? (
                                <><img src={localConfig.logoUrl} alt="Logo Preview" className="h-full object-contain mb-1" /><button onClick={() => setLocalConfig({...localConfig, logoUrl: undefined})} className="absolute top-1 right-1 p-1 bg-red-100 text-red-500 rounded-full hover:bg-red-200" title="Logo entfernen"><X size={12} /></button></>
                            ) : (
                                <><Image size={24} className="text-slate-300 mb-2" /><span className="text-[10px] text-slate-400">Bild hochladen</span></>
                            )}
                            {!isUploading && <input type="file" accept="image/*" onChange={handleLogoUpload} className="absolute inset-0 opacity-0 cursor-pointer" />}
                        </div>
                        <p className="text-[9px] text-slate-400 mt-2 italic">Speichert in: bucket/images/logos/</p>
                    </div>
                    <div className="col-span-1 md:col-span-2">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Begrüßungstext</label>
                        <textarea value={localConfig.welcomeText || ''} onChange={e => setLocalConfig({...localConfig, welcomeText: e.target.value})} className="w-full h-32 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-[var(--color-primary)] outline-none bg-white resize-none text-sm text-slate-800" placeholder="Ein kurzer Text, der auf der Startseite angezeigt wird..." />
                    </div>
                </div>
                <div className="mt-4"><ToggleSwitch checked={localConfig.showOwnerOnStart} onChange={() => setLocalConfig({...localConfig, showOwnerOnStart: !localConfig.showOwnerOnStart})} label="Inhaber auf Startseite anzeigen" /></div>
                <div className="mt-6 pt-4 border-t border-slate-100">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-xs font-bold text-slate-500 uppercase mb-3 flex items-center gap-2 text-slate-500"><Share2 size={12} /> Social Logins</label>
                          <div className="flex flex-col gap-4">
                                <ToggleSwitch checked={(localConfig.socialProviders || []).includes('google')} onChange={() => toggleSocialProvider('google')} label="Google Login" />
                                <ToggleSwitch 
                                    checked={false} 
                                    onChange={() => {}} 
                                    label={
                                        <span className="flex items-center">
                                            Apple Login <span className="text-[10px] text-slate-400 font-normal ml-1">(aktuell nicht verfügbar)</span>
                                        </span>
                                    } 
                                    disabled={true} 
                                />
                                <ToggleSwitch checked={(localConfig.socialProviders || []).includes('facebook')} onChange={() => toggleSocialProvider('facebook')} label="Facebook Login" />
                          </div>
                        </div>
                        <div><label className="block text-xs font-bold text-slate-500 uppercase mb-3 flex items-center gap-2 text-slate-500"><UserCheck size={12} /> Gastzugang</label><ToggleSwitch checked={localConfig.allowGuestReservations} onChange={() => setLocalConfig({...localConfig, allowGuestReservations: !localConfig.allowGuestReservations})} label="Gast-Reservierung ohne Login erlauben" /></div>
                    </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'DESIGN' && (
            <div className="space-y-6">
              <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                  <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2 pb-2 border-b border-slate-100"><Palette size={16} className="text-[var(--color-primary)]" /> Design & Gasterlebnis</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                          <label className="block text-xs font-bold text-slate-500 uppercase mb-3">Theme-Modus</label>
                          <div className="grid grid-cols-2 gap-2">
                          <button onClick={() => handleModeChange('MODERN')} className={`p-3 border rounded-lg flex flex-col items-center gap-2 transition-all relative ${localConfig.theme?.mode === 'MODERN' ? 'bg-slate-50 border-[var(--color-primary)] ring-1 ring-[var(--color-primary)]' : 'bg-white border-slate-200 hover:bg-slate-50'}`}><Layout size={20} className="text-slate-600" /><span className="text-[10px] font-bold text-slate-700">Modern</span></button>
                          <button onClick={() => handleModeChange('SIMPLE')} className={`p-3 border rounded-lg flex flex-col items-center gap-2 transition-all relative ${localConfig.theme?.mode === 'SIMPLE' ? 'bg-slate-50 border-[var(--color-primary)] ring-1 ring-[var(--color-primary)]' : 'bg-white border-slate-200 hover:bg-slate-50'}`}><Square size={20} className="text-slate-500" /><span className="text-[10px] font-bold text-slate-700">Schlicht</span></button>
                          </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                           <div><label className="block text-xs font-bold text-slate-500 uppercase mb-2">Akzent 1</label><input type="color" value={localConfig.theme?.primaryColor || '#f59e0b'} onChange={(e) => setLocalConfig({...localConfig, theme: { ...localConfig.theme, primaryColor: e.target.value }})} className="w-full h-10 p-1 border border-slate-300 rounded cursor-pointer bg-white" /></div>
                          <div><label className="block text-xs font-bold text-slate-500 uppercase mb-2">Akzent 2</label><input type="color" value={localConfig.theme?.secondaryColor || '#0f172a'} onChange={(e) => setLocalConfig({...localConfig, theme: { ...localConfig.theme, secondaryColor: e.target.value }})} className="w-full h-10 p-1 border border-slate-300 rounded cursor-pointer bg-white" /></div>
                      </div>
                  </div>
                  <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
                      <label className="block text-xs font-bold text-slate-500 uppercase">Helles / Dunkles Design</label>
                      <div className="flex items-center border border-slate-200 rounded-full p-1 bg-slate-50">
                          <button onClick={() => setLocalConfig({ ...localConfig, theme: { ...localConfig.theme, darkMode: false } })} className={`p-2 rounded-full transition-all ${!localConfig.theme?.darkMode ? 'bg-white shadow text-amber-500' : 'text-slate-400 hover:text-slate-600'}`}><Sun size={16} /></button>
                          <button onClick={() => setLocalConfig({ ...localConfig, theme: { ...localConfig.theme, darkMode: true } })} className={`p-2 rounded-full transition-all ${localConfig.theme?.darkMode ? 'bg-slate-700 shadow text-white' : 'text-slate-400 hover:text-slate-600'}`}><Moon size={16} /></button>
                      </div>
                  </div>
              </div>

              <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                  <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2 pb-2 border-b border-slate-100"><Calendar size={16} className="text-[var(--color-primary)]" /> Terminkalender</h3>
                  <p className="text-xs text-slate-500 mb-4">Hier legen Sie die Darstellung der Buchungsblöcke im Kalender fest.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-xs font-bold text-slate-500 uppercase text-slate-500">Bestätigte Termine</label>
                        <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg border border-slate-100">
                          <input 
                            type="color" 
                            value={localConfig.theme?.calendarConfirmedColor || localConfig.theme?.secondaryColor} 
                            onChange={(e) => setLocalConfig({...localConfig, theme: { ...localConfig.theme, calendarConfirmedColor: e.target.value }})} 
                            className="w-10 h-10 p-1 border border-slate-300 rounded cursor-pointer bg-white" 
                          />
                          <div className="flex-1">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Vorschau</span>
                            <div className="h-6 rounded-md mt-1 shadow-sm border border-black/5" style={{ backgroundColor: localConfig.theme?.calendarConfirmedColor || localConfig.theme?.secondaryColor }}></div>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs font-bold text-slate-500 uppercase text-slate-500">Angefragte Termine (Pending)</label>
                        <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg border border-slate-100">
                          <input 
                            type="color" 
                            value={localConfig.theme?.calendarPendingColor || '#f97316'} 
                            onChange={(e) => setLocalConfig({...localConfig, theme: { ...localConfig.theme, calendarPendingColor: e.target.value }})} 
                            className="w-10 h-10 p-1 border border-slate-300 rounded cursor-pointer bg-white" 
                          />
                          <div className="flex-1">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Vorschau</span>
                            <div className="h-6 rounded-md mt-1 shadow-sm border border-black/5" style={{ backgroundColor: localConfig.theme?.calendarPendingColor || '#f97316' }}></div>
                          </div>
                        </div>
                      </div>
                  </div>
              </div>
            </div>
          )}

          {activeTab === 'SCHEDULE' && (
              <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm space-y-8">
                <div>
                  <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2 pb-2 border-b border-slate-100"><Clock size={16} className="text-[var(--color-primary)]" /> Öffnungszeiten & Regeln</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div><label className="block text-xs font-bold text-slate-500 uppercase mb-2 text-slate-500">Reservierungsart</label><div className="flex gap-2">
                          <button onClick={() => setLocalConfig({...localConfig, reservationMode: 'GENERAL'})} className={`flex-1 flex flex-col items-center gap-1 p-2 border rounded-md text-xs transition-all ${localConfig.reservationMode === 'GENERAL' ? 'bg-white border-[var(--color-primary)] text-[var(--primary-color)] shadow-sm' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}><EyeOff size={18} /> Automatisch</button>
                          <button onClick={() => setLocalConfig({...localConfig, reservationMode: 'SPECIFIC_CATEGORY'})} className={`flex-1 flex flex-col items-center gap-1 p-2 border rounded-md text-xs transition-all ${localConfig.reservationMode === 'SPECIFIC_CATEGORY' ? 'bg-white border-[var(--color-primary)] text-[var(--color-primary)] shadow-sm' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}><Filter size={18} /> Nach Bereich</button>
                          </div></div>
                      <div><label className="block text-xs font-bold text-slate-500 uppercase mb-2 text-slate-500">Ansicht für Gäste</label><div className="flex gap-2">
                          <button onClick={() => setLocalConfig({...localConfig, guestViewMode: 'MAP'})} className={`flex-1 flex flex-col items-center gap-1 p-2 border rounded-md text-xs transition-all ${localConfig.guestViewMode === 'MAP' ? 'bg-white border-[var(--color-primary)] text-[var(--color-primary)] shadow-sm' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}><Eye size={18} /> Mit Floorplan</button>
                          <button onClick={() => setLocalConfig({...localConfig, guestViewMode: 'SIMPLE'})} className={`flex-1 flex flex-col items-center gap-1 p-2 border rounded-md text-xs transition-all ${localConfig.guestViewMode === 'SIMPLE' ? 'bg-white border-[var(--color-primary)] text-[var(--color-primary)] shadow-sm' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}><List size={18} /> Nur Termine</button>
                          </div></div>
                  </div>
                  <div className="border-t border-slate-100 pt-4"><ToggleSwitch checked={localConfig.requireReservationApproval} onChange={() => setLocalConfig({...localConfig, requireReservationApproval: !localConfig.requireReservationApproval})} label="Einzelbuchungen müssen bestätigt werden" /></div>
                </div>

                <div className="bg-emerald-50/50 p-5 rounded-xl border border-emerald-100">
                    <h3 className="text-sm font-bold text-emerald-900 mb-4 flex items-center gap-2 pb-2 border-b border-emerald-100/50">
                        <Repeat size={16} /> Serientermine (Abos)
                    </h3>
                    <div className="space-y-4">
                        <ToggleSwitch 
                            checked={localConfig.allowRecurringBookings} 
                            onChange={() => setLocalConfig({...localConfig, allowRecurringBookings: !localConfig.allowRecurringBookings})} 
                            label="Wöchentliche Serientermine für Gäste erlauben" 
                        />
                        {localConfig.allowRecurringBookings && (
                            <div className="ml-8 border-l-2 border-emerald-200 pl-4 animate-fade-in">
                                <ToggleSwitch 
                                    checked={localConfig.recurringRequireApproval} 
                                    onChange={() => setLocalConfig({...localConfig, recurringRequireApproval: !localConfig.recurringRequireApproval})} 
                                    label="Serientermine benötigen IMMER Admin-Bestätigung" 
                                />
                                <p className="text-[10px] text-emerald-700 mt-2 italic">
                                    Hinweis: Gäste können Serien bis max. 1 Jahr im Voraus buchen. Kollisionen werden automatisch erkannt und übersprungen.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="space-y-2 border-t border-slate-100 pt-4">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-3 text-slate-500">Wochenplan</label>
                  {days.map(day => {
                    const schedule = localConfig.schedule[day] || { open: '09:00', close: '22:00', isClosed: false };
                    return (
                      <div key={day} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-3 border-b border-slate-50 last:border-0">
                        <div className="w-full sm:w-24 font-medium text-slate-700 text-sm">{getDayLabel(day)}</div>
                        <div className="flex-1 flex items-center justify-between sm:justify-start gap-4">
                          <div className="flex items-center gap-2">
                            <button onClick={() => updateSchedule(day, 'isClosed', !schedule.isClosed)} className={`w-10 h-5 rounded-full relative transition-colors flex-shrink-0 ${!schedule.isClosed ? 'bg-green-500' : 'bg-slate-300'}`} title={schedule.isClosed ? "Geschlossen" : "Geöffnet"}><div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-all ${!schedule.isClosed ? 'left-6' : 'left-1'}`} /></button>
                            <span className={`text-xs w-20 font-medium ${schedule.isClosed ? 'text-red-500' : 'text-green-600'}`}>{schedule.isClosed ? 'Geschlossen' : 'Geöffnet'}</span>
                          </div>
                          {!schedule.isClosed && (
                            <div className="flex items-center gap-2">
                              <input type="time" value={schedule.open} onChange={(e) => updateSchedule(day, 'open', e.target.value)} className="px-2 py-1 text-sm border border-slate-300 rounded focus:ring-1 focus:ring-[var(--color-primary)] outline-none bg-white w-24 text-slate-800" />
                              <span className="text-slate-400 text-xs">-</span>
                              <input type="time" value={schedule.close} onChange={(e) => updateSchedule(day, 'close', e.target.value)} className="px-2 py-1 text-sm border border-slate-300 rounded focus:ring-1 focus:ring-[var(--color-primary)] outline-none bg-white w-24 text-slate-800" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
          )}

          {activeTab === 'CATEGORIES' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                 <p className="text-xs text-slate-500">Definiere eigene Sportarten und Reservierungsdauer.</p>
                 <button onClick={handleAddCategory} className="text-xs flex items-center gap-1 px-4 py-2 rounded-full font-bold shadow-sm transition-all hover:-translate-y-0.5" style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}><Plus size={16} /> Sportart hinzufügen</button>
              </div>
              <div className="space-y-3">
                {localCategories.map((cat) => (
                  <div key={cat.id} className="flex items-center gap-3 p-4 border border-slate-200 rounded-xl bg-white hover:shadow-md transition-all group border-l-4" style={{ borderLeftColor: cat.borderColor }}>
                     <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-3 items-end">
                        <div className="md:col-span-4">
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-0.5">Name der Sportart</label>
                          <input type="text" value={cat.name} onChange={e => updateCategory(cat.id, 'name', e.target.value)} className="w-full text-sm font-bold bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 outline-none focus:bg-white focus:ring-2 focus:ring-[var(--color-primary)] transition-all text-slate-800" />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-0.5 text-slate-400">Farben</label>
                          <div className="flex gap-1.5">
                            <div className="relative group/picker">
                              <input type="color" value={cat.color} onChange={e => updateCategory(cat.id, 'color', e.target.value)} className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5 bg-white overflow-hidden shadow-sm" title="Hintergrundfarbe" />
                              <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[8px] py-0.5 px-1 rounded opacity-0 group-hover/picker:opacity-100 whitespace-nowrap pointer-events-none">BG</div>
                            </div>
                            <div className="relative group/picker">
                              <input type="color" value={cat.borderColor} onChange={e => updateCategory(cat.id, 'borderColor', e.target.value)} className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5 bg-white overflow-hidden shadow-sm" title="Rahmenfarbe" />
                              <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[8px] py-0.5 px-1 rounded opacity-0 group-hover/picker:opacity-100 whitespace-nowrap pointer-events-none">Rahmen</div>
                            </div>
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-0.5 text-slate-400">Icon</label>
                          <select value={cat.icon} onChange={e => updateCategory(cat.id, 'icon', e.target.value)} className="w-full text-sm font-bold bg-slate-50 border border-slate-200 rounded-lg px-2 py-2 outline-none focus:bg-white focus:ring-2 focus:ring-[var(--color-primary)] transition-all text-slate-800">
                            <option value="⚽">⚽ Fußball</option><option value="🎾">🎾 Tennis</option><option value="🏐">🏐 Volleyball</option><option value="🏀">🏀 Basketball</option><option value="🏸">🏸 Badminton</option><option value="🎱">🎱 Billard</option><option value="🏓">🏓 Tischtennis</option><option value="🎳">🎳 Bowling</option><option value="🏈">🏈 Football</option><option value="⚾">⚾ Baseball</option><option value="🏒">🏒 Eishockey</option><option value="🏑">🏑 Feldhockey</option><option value="🏏">🏏 Cricket</option><option value="🏉">🏉 Rugby</option><option value="🏋️">🏋️ Fitness</option><option value="🏃">🏃 Laufen</option><option value="🎯">🎯 Dart</option><option value="🏆">🏆 Trophäe</option>
                          </select>
                        </div>
                         <div className="md:col-span-2">
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-0.5 text-slate-400">Dauer</label>
                          <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg overflow-hidden group-focus-within:ring-2 group-focus-within:ring-[var(--color-primary)] transition-all">
                            <button onClick={() => updateCategory(cat.id, 'minDuration', Math.max(15, (cat.minDuration || 60) - 15))} className="w-8 h-9 flex items-center justify-center hover:bg-slate-100 border-r border-slate-200 text-slate-600"><Minus size={14} /></button>
                            <input type="number" min="15" step="15" value={cat.minDuration || 90} onChange={e => updateCategory(cat.id, 'minDuration', parseInt(e.target.value))} className="flex-1 w-full text-center text-xs font-black bg-transparent outline-none appearance-none text-slate-800" />
                            <button onClick={() => updateCategory(cat.id, 'minDuration', (cat.minDuration || 60) + 15)} className="w-8 h-9 flex items-center justify-center hover:bg-slate-100 border-l border-slate-200 text-slate-600"><Plus size={14} /></button>
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-0.5 text-slate-400">Teams</label>
                          <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg overflow-hidden group-focus-within:ring-2 group-focus-within:ring-[var(--color-primary)] transition-all">
                            <button onClick={() => updateCategory(cat.id, 'defaultSeats', Math.max(1, (cat.defaultSeats || 2) - 1))} className="w-8 h-9 flex items-center justify-center hover:bg-slate-100 border-r border-slate-200 text-slate-600"><Minus size={14} /></button>
                            <input type="number" min="1" max="50" value={cat.defaultSeats || 2} onChange={e => updateCategory(cat.id, 'defaultSeats', parseInt(e.target.value))} className="flex-1 w-full text-center text-sm font-black bg-transparent outline-none appearance-none text-slate-800" />
                            <button onClick={() => updateCategory(cat.id, 'defaultSeats', (cat.defaultSeats || 2) + 1)} className="w-8 h-9 flex items-center justify-center hover:bg-slate-100 border-l border-slate-200 text-slate-600"><Plus size={14} /></button>
                          </div>
                        </div>
                     </div>
                     <div className="flex-shrink-0 ml-2">
                        <button type="button" onClick={() => deleteCategory(cat.id)} className="p-2.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="Sportart entfernen"><Trash2 size={18} /></button>
                     </div>
                  </div>
                ))}
                {localCategories.length === 0 && (
                   <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-slate-200">
                     <LayoutGrid size={40} className="mx-auto text-slate-200 mb-2" />
                     <p className="text-slate-400 text-sm font-medium">Noch keine Sportarten definiert.</p>
                   </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'PACKAGES' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                 <div><h3 className="text-sm font-bold text-slate-800">Paket-Konfiguration</h3><p className="text-xs text-slate-500">Erstelle spezielle Buchungsoptionen für deine Sportarten.</p></div>
                 <button onClick={handleAddPackage} className="text-xs flex items-center gap-1 px-4 py-2 rounded-full font-bold shadow-sm transition-all hover:-translate-y-0.5" style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}><Plus size={16} /> Paket hinzufügen</button>
              </div>
              <div className="space-y-4">
                {(localConfig.packages || []).map((pkg, idx) => (
                  <div key={pkg.id} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden group">
                    <div className="flex items-center justify-between mb-6 border-b border-slate-50 pb-4">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center text-slate-400 font-black text-xs">#{idx + 1}</div>
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Paket-Konfiguration</span>
                        </div>
                        <button onClick={() => deletePackage(pkg.id)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-500 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"><Trash2 size={14} /> Paket entfernen</button>
                    </div>

                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                        <div className="md:col-span-6">
                          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Paketname</label>
                          <input type="text" value={pkg.name} onChange={e => updatePackage(pkg.id, { name: e.target.value })} className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-[var(--color-primary)] font-bold bg-white text-slate-800" placeholder="z.B. Early Bird Special" />
                        </div>
                        <div className="md:col-span-4">
                          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Sportart</label>
                          <select value={pkg.categoryId} onChange={e => updatePackage(pkg.id, { categoryId: e.target.value })} className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-[var(--color-primary)] bg-white font-medium text-slate-800"><option value="">Wähle eine Sportart...</option>{localCategories.map(cat => (<option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>))}</select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Icon</label>
                          <select value={pkg.icon} onChange={e => updatePackage(pkg.id, { icon: e.target.value })} className="w-full text-left text-sm border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-[var(--color-primary)] bg-white text-slate-800"><option value="🎁">🎁 Geschenk</option><option value="🎂">🎂 Geburtstag</option><option value="🥂">🥂 Anstoßen</option><option value="🎈">🎈 Ballon</option><option value="🎊">🎊 Konfetti</option><option value="🍕">🍕 Pizza-Party</option><option value="🍔">🍔 Burger-Abend</option><option value="🥤">🥤 Getränke-Flat</option><option value="🏆">🏆 Turnier</option><option value="🌟">🌟 VIP Special</option><option value="🔥">🔥 Hot Deal</option><option value="⚡">⚡ Power-Session</option><option value="🕺">🕺 Event</option></select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-slate-50 rounded-xl border border-slate-100">
                         <div className="space-y-1"><label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 text-slate-500">Dauer (Minuten)</label><div className="flex items-center border border-slate-300 rounded-lg overflow-hidden bg-white shadow-sm max-w-[200px]"><button onClick={() => updatePackage(pkg.id, { durationMinutes: Math.max(15, (pkg.durationMinutes || 60) - 15) })} className="w-10 h-10 flex items-center justify-center bg-slate-50 hover:bg-slate-100 border-r border-slate-200 text-slate-600 transition-colors"><Minus size={16} /></button><input type="number" value={pkg.durationMinutes || 60} onChange={e => updatePackage(pkg.id, { durationMinutes: parseInt(e.target.value) })} className="flex-1 w-full text-center text-sm font-bold text-slate-800 outline-none appearance-none bg-transparent" /><button onClick={() => updatePackage(pkg.id, { durationMinutes: (pkg.durationMinutes || 60) + 15 })} className="w-10 h-10 flex items-center justify-center bg-slate-50 hover:bg-slate-100 border-l border-slate-200 text-slate-600 transition-colors"><Plus size={16} /></button></div></div>
                         <div className="space-y-1"><label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 text-slate-500">Std. Spieler</label><div className="flex items-center border border-slate-300 rounded-lg overflow-hidden bg-white shadow-sm max-w-[200px]"><button onClick={() => updatePackage(pkg.id, { defaultPartySize: Math.max(1, (pkg.defaultPartySize || 2) - 1) })} className="w-10 h-10 flex items-center justify-center bg-slate-50 hover:bg-slate-100 border-r border-slate-200 text-slate-600 transition-colors"><Minus size={16} /></button><input type="number" value={pkg.defaultPartySize || 2} onChange={e => updatePackage(pkg.id, { defaultPartySize: parseInt(e.target.value) })} className="flex-1 w-full text-center text-sm font-bold text-slate-800 outline-none appearance-none bg-transparent" /><button onClick={() => updatePackage(pkg.id, { defaultPartySize: (pkg.defaultPartySize || 2) + 1 })} className="w-10 h-10 flex items-center justify-center bg-slate-50 hover:bg-slate-100 border-l border-slate-200 text-slate-600 transition-colors"><Plus size={16} /></button></div></div>
                      </div>

                      <div className="space-y-4">
                        <div><label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2 text-slate-500"><CalendarDays size={14} /> Verfügbare Wochentage</label><div className="flex flex-wrap gap-2">{days.map(day => (<button key={day} onClick={() => togglePackageDay(pkg.id, day)} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all border shadow-sm ${pkg.allowedDays.includes(day) ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]' : 'bg-white text-slate-400 border-slate-100 hover:border-slate-300'}`}>{getDayLabel(day)}</button>))}</div></div>
                        <div className="pt-4 border-t border-slate-100">
                           <div className="flex items-center justify-between mb-4"><label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2 text-slate-500"><Clock size={14} /> Zeitliche Einschränkung</label><ToggleSwitch checked={isTimeRestricted(pkg)} onChange={() => { if (!isTimeRestricted(pkg)) { updatePackage(pkg.id, { startTime: arenaBounds.min, endTime: arenaBounds.max }); } else { updatePackage(pkg.id, { startTime: '00:00', endTime: '23:59' }); } }} /></div>
                           {isTimeRestricted(pkg) && (
                              <div className="grid grid-cols-2 gap-4 animate-fade-in">
                                <div><label className="block text-[10px] font-bold text-slate-400 mb-1">Von</label><input type="time" min={arenaBounds.min} max={pkg.endTime} value={pkg.startTime || arenaBounds.min} onChange={e => updatePackage(pkg.id, { startTime: e.target.value })} className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-[var(--color-primary)] bg-white font-bold text-slate-800" /></div>
                                <div><label className="block text-[10px] font-bold text-slate-400 mb-1">Bis</label><input type="time" min={pkg.startTime} max={arenaBounds.max} value={pkg.endTime || arenaBounds.max} onChange={e => updatePackage(pkg.id, { endTime: e.target.value })} className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-[var(--color-primary)] bg-white font-bold text-slate-800" /></div>
                                <div className="col-span-2"><p className="text-[10px] text-slate-400 italic">Die Arena öffnet frühestens um {arenaBounds.min} Uhr und schließt spätestens um {arenaBounds.max} Uhr.</p></div>
                              </div>
                           )}
                        </div>

                        <div className="pt-4 border-t border-slate-100">
                          <button
                            type="button"
                            onClick={() => setExpandedPackageInfo(prev => ({ ...prev, [pkg.id]: !prev[pkg.id] }))}
                            className="flex items-center gap-2 text-[10px] font-black text-slate-800 uppercase tracking-widest hover:text-[var(--color-primary)] transition-colors"
                          >
                            <Info size={14} /> Erweiterte Informationen (Pro Editor)
                            {expandedPackageInfo[pkg.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>
                          
                          {expandedPackageInfo[pkg.id] && (
                            <div className="mt-4 animate-fade-in-up">
                              <RichTextEditor 
                                value={pkg.extendedInfo || ''} 
                                onChange={(val) => updatePackage(pkg.id, { extendedInfo: val })}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {(localConfig.packages || []).length === 0 && (<div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-slate-200"><Package size={48} className="mx-auto text-slate-100 mb-4" /><p className="text-slate-400 text-sm font-medium">Noch keine speziellen Pakete erstellt.</p></div>)}
              </div>
            </div>
          )}

          {activeTab === 'UPGRADE' && (
            <div className="flex flex-col items-center justify-center p-8 text-center space-y-8 animate-fade-in min-h-[50vh]">
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 w-20 h-20 rounded-3xl flex items-center justify-center shadow-xl shadow-indigo-200 mb-2">
                    <Rocket size={40} className="text-white" />
                </div>
                
                <div className="max-w-md space-y-2">
                    <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Upgrade dein Business</h3>
                    <p className="text-slate-500 font-medium text-sm leading-relaxed">
                        Wir arbeiten mit Hochdruck an neuen Features, um dein Business noch erfolgreicher zu machen. Diese Funktionen werden in Kürze freigeschaltet.
                    </p>
                </div>

                <div className="w-full max-w-md bg-slate-50 border border-slate-200 rounded-2xl p-2 space-y-1 opacity-60 pointer-events-none grayscale select-none">
                    {[
                        "SMS Benachrichtigung",
                        "Unlimited Email Benachrichtigung",
                        "An- & Bezahlungen",
                        "neue Designtemplates",
                        "Komplette Kassensysteme"
                    ].map((feature, i) => (
                        <div key={i} className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-xl shadow-sm">
                            <span className="font-bold text-slate-400 text-sm">{feature}</span>
                            <ToggleSwitch checked={false} onChange={() => {}} disabled={true} />
                        </div>
                    ))}
                </div>
                
                <div className="px-4 py-2 bg-amber-50 text-amber-600 text-[10px] font-black uppercase tracking-widest rounded-lg border border-amber-100">
                    Coming Soon
                </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-white hover:shadow-sm border border-transparent hover:border-slate-200 rounded transition-all">Abbrechen</button>
          <button onClick={handleSave} className="px-6 py-2 bg-[var(--color-secondary)] text-white rounded font-medium hover:opacity-90 shadow-md flex items-center gap-2"><Save size={16} /> Speichern</button>
        </div>
      </div>
    </div>
  );
};